import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { CardWrapper } from '~/components/cards/CardWrapper';
import { cn } from '~/utils';
import {
  statusBadgeClasses,
  type StatusBadgeTone,
} from '~/components/ui/statusBadgeClasses';
import { ExtractionStatus, UnitStatus } from '~/graphql/graphql';
import type {
  ExtractedDocumentView,
  UnitProgress,
} from '~/api/documentExtraction';
import { isFlagged } from './importHandoff';
import { sourceKeyOf, sourceLabelerOf } from './sourceLabels';

const RUNNING_UNIT_STATUSES: readonly UnitStatus[] = [
  UnitStatus.Pending,
  UnitStatus.Inferring,
];

const STATUS_TONES: Partial<Record<ExtractionStatus, StatusBadgeTone>> = {
  [ExtractionStatus.Complete]: 'accepted',
  [ExtractionStatus.NeedsReview]: 'pending',
  [ExtractionStatus.Failed]: 'declined',
};

function isRunning(unit: UnitProgress): boolean {
  return RUNNING_UNIT_STATUSES.includes(unit.status);
}

function percentOf(covered: number, expected: number): number {
  if (expected <= 0) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round((covered / expected) * 100)));
}

export function DocumentProgress({
  document,
}: {
  document: ExtractedDocumentView;
}) {
  const { t } = useTranslation('document-extraction');

  const percent = percentOf(
    document.coveredPositionCount,
    document.expectedPositionCount
  );
  const flagged = document.items.filter(isFlagged).length;
  const labelOf = sourceLabelerOf(document.units);
  const verified = document.items.length - flagged;

  return (
    <CardWrapper>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              data-testid="status"
              className={statusBadgeClasses(
                STATUS_TONES[document.status] ?? 'info'
              )}
            >
              {t(`status.${document.status}`)}
            </span>
            <span data-testid="coverage" className="text-sm text-neut-sec">
              {t('progress.coverage', {
                covered: document.coveredPositionCount,
                expected: document.expectedPositionCount,
              })}
            </span>
            {document.items.length > 0 && (
              <span
                data-testid="verified"
                data-flagged={flagged}
                className={cn(
                  'text-sm',
                  flagged === 0 ? 'text-success-700' : 'text-warning-800'
                )}
              >
                {t('progress.verified', {
                  verified,
                  total: document.items.length,
                })}
                {flagged > 0 &&
                  ` · ${t('progress.flagged', { count: flagged })}`}
              </span>
            )}
          </div>
          <span className="text-sm font-medium text-neut-prim">{percent}%</span>
        </div>

        <div
          className="h-2 w-full overflow-hidden rounded-full bg-neut-200"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full bg-prim transition-[width] duration-300"
            style={{ width: `${percent}%` }}
          />
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
          {[
            ['progress.vendor', document.vendorName],
            ['progress.price_basis', document.priceBasis],
            ['progress.currency', document.currency],
          ].map(([key, value]) => (
            <div key={key} className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-neut-sec">
                {t(key as string)}
              </dt>
              <dd className="text-sm text-neut-prim">
                {value ?? t('progress.unknown')}
              </dd>
            </div>
          ))}
        </dl>

        {document.failureReason && (
          <p
            data-testid="failure-reason"
            className="flex items-center gap-2 rounded border border-error-200 bg-error-100 px-3 py-2 text-sm text-error-700"
          >
            <AlertCircle size={16} />
            {t(`failure_reason.${document.failureReason}`)}
          </p>
        )}

        {document.ambiguities.length > 0 && (
          <ul
            data-testid="document-ambiguities"
            className="flex flex-col gap-1 rounded border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-800"
          >
            {document.ambiguities.map((note) => (
              <li key={note} className="flex items-center gap-2">
                <AlertTriangle size={14} />
                {note}
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-col gap-2">
          <span className="text-xs uppercase tracking-wide text-neut-sec">
            {t('progress.units')}
          </span>
          <ul className="flex flex-wrap gap-2">
            {document.units.map((unit) => (
              <li
                key={sourceKeyOf(unit.documentId, unit.unitKey)}
                data-testid="unit-chip"
                data-running={String(isRunning(unit))}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs',
                  isRunning(unit)
                    ? 'animate-pulse border-prim-200 bg-prim-50 text-prim-800'
                    : 'border-neut-border bg-neut-50 text-neut-700'
                )}
              >
                {labelOf(unit.documentId, unit.label)} ·{' '}
                {unit.coveredPositionCount}/{unit.expectedPositionCount} ·{' '}
                {t(`unit_status.${unit.status}`)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </CardWrapper>
  );
}
